import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cake, ArrowRight } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import UserAvatar from "./UserAvatar";

const DAY_LABELS = [
  "Today!",
  "Tomorrow",
  "In 2 days",
  "In 3 days",
  "In 4 days",
  "In 5 days",
  "In 6 days",
];

/** Horizontal rail of friends whose birthdays land in the next 7 days. Only
 *  renders when there's something to celebrate, and only for signed-in users.
 *  Tapping a friend jumps to their profile where the Send-Birthday-Blessing
 *  flow lives. */
export default function BirthdayRail() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/friends/birthdays-this-week")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [user]);

  if (!user || items.length === 0) return null;

  const headline = items.some((f) => f.is_today)
    ? `Birthdays today · ${items.filter((f) => f.is_today).length}`
    : "Birthdays this week";

  return (
    <section
      id="birthday-rail"
      className="my-6 rounded-3xl p-4 md:p-5 border border-terracotta/30 bg-gradient-to-r from-terracotta/10 to-amber-50"
      data-testid="birthday-rail"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 px-2.5 h-7 text-terracotta hover:text-terracotta/80 text-[10px] tracking-[0.2em] font-semibold uppercase">
          <Cake size={12} weight="fill" /> {headline}
        </span>
        <span className="text-sm font-heading text-terracotta-700">
          Drop a Scripture blessing on their wall.
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {items.map((f) => (
          <Link
            key={f.id}
            to={`/u/${f.id}`}
            data-testid={`birthday-card-${f.id}`}
            className="shrink-0 w-44 bg-white rounded-2xl border border-cream-300 p-3 hover:shadow-md hover:-translate-y-0.5 transition-all"
          >
            <div className="aspect-square relative rounded-xl overflow-hidden bg-gradient-to-br from-terracotta to-terracotta-600 grid place-items-center">
              <UserAvatar
                user={{ name: f.name, avatar_url: f.avatar_url }}
                size={72}
              />
              {f.is_today && (
                <span className="absolute -top-1 -right-1 text-2xl rotate-12" aria-hidden>🎂</span>
              )}
            </div>
            <div className="mt-2">
              <div className="font-heading text-sm text-forest truncate">{f.name}</div>
              <div className="text-[10px] text-muted_ink truncate flex items-center gap-1">
                <span className={f.is_today ? "text-terracotta font-semibold" : ""}>
                  {DAY_LABELS[f.days_away] || `In ${f.days_away} days`}
                </span>
                <ArrowRight size={10} weight="bold" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
